import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { API } from "@/lib/api";
import {
  BellRing, Building2, CalendarClock, CheckCircle2, DollarSign, FileCheck2, Hash, Mail, Paperclip, Pencil,
  Phone, RefreshCw, Send, StickyNote, User, UserCircle2, Users,
} from "lucide-react";
import { STATUS_META, RECURRENCE_LABELS, overdueLabel, nextReminderText, when, fmtStamp, notifyScopeText, chanText } from "@/pages/dashboard/renewalsMeta";
import { V } from "@/lib/vocab";

const Row = ({ icon: Icon, label, children, testid }) => (
  <div className="flex items-start gap-3 py-2.5 border-b border-sand-100 last:border-0" data-testid={testid}>
    <Icon className="h-4 w-4 text-ink/40 mt-0.5 shrink-0" strokeWidth={1.75} />
    <div className="w-28 shrink-0 text-xs uppercase tracking-wider font-bold text-ink/50 pt-0.5">{label}</div>
    <div className="min-w-0 flex-1 text-sm text-ink">{children}</div>
  </div>
);

// Everything about one obligation in one place — the card only shows the headline.
export function RenewalDetailSheet({ item, tenantId, readOnly, onClose, onEdit, onComplete, onTest }) {
  const i = item;
  const open = !!i;
  if (!i) return <Sheet open={false} />;
  const v = i.vendor || {};
  const hasVendor = Object.values(v).some(Boolean) || i.vendor_name;
  const leads = [...(i.effective_lead_days || i.lead_days || [])].sort((a, b) => b - a);
  const spent = new Set(i.nudged || []);
  const status = STATUS_META[i.status] || STATUS_META.upcoming;
  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto p-0" data-testid="renewal-detail-sheet">
        <div className="p-5 sm:p-6 border-b border-sand-200 bg-sand-50/60">
          <SheetHeader className="text-left space-y-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[11px] font-bold uppercase tracking-wider rounded-full border px-2 py-0.5 ${status.cls}`} data-testid="renewal-detail-status">{status.label}</span>
              <span className="text-xs text-muted-foreground">{i.category_label}</span>
            </div>
            <SheetTitle className="font-heading text-xl font-extrabold text-ink leading-tight" data-testid="renewal-detail-title">{i.title}</SheetTitle>
            <SheetDescription className="text-sm text-ink/70" data-testid="renewal-detail-when">
              {i.status === "overdue" ? "Was due" : "Due"} {when(i).replace(/^was due /, "")} · {RECURRENCE_LABELS[i.recurrence] || i.recurrence}
            </SheetDescription>
          </SheetHeader>
          {!readOnly && (
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <button data-testid="renewal-detail-complete" onClick={() => onComplete(i)}
                className="rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 px-3.5 py-1.5 text-xs font-bold hover:bg-emerald-100 transition-colors inline-flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5" /> Mark renewed
              </button>
              <button data-testid="renewal-detail-edit" onClick={() => onEdit(i)}
                className="rounded-full border border-sand-300 bg-white px-3.5 py-1.5 text-xs font-bold text-ink hover:border-ink transition-colors inline-flex items-center gap-1.5">
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
              <button data-testid="renewal-detail-test" onClick={() => onTest(i)}
                className="rounded-full border border-sand-300 bg-white px-3.5 py-1.5 text-xs font-bold text-ink hover:border-ink transition-colors inline-flex items-center gap-1.5">
                <Send className="h-3.5 w-3.5" /> Send me a test reminder
              </button>
            </div>
          )}
        </div>

        <div className="p-5 sm:p-6">
          <Row icon={CalendarClock} label="Due" testid="renewal-detail-due">{i.due_on} <span className="text-ink/60">· {i.status === "overdue" ? `${Math.abs(i.days_out)} day${Math.abs(i.days_out) === 1 ? "" : "s"} overdue` : `in ${i.days_out} day${i.days_out === 1 ? "" : "s"}`}</span></Row>
          <Row icon={RefreshCw} label="Cycle">{RECURRENCE_LABELS[i.recurrence] || i.recurrence}</Row>
          <Row icon={UserCircle2} label="Owner" testid="renewal-detail-owner">{i.owner_name || <span className="text-ink/60">The whole management team</span>}</Row>
          {i.cost != null && i.cost !== "" && <Row icon={DollarSign} label="Cost" testid="renewal-detail-cost">${Number(i.cost).toLocaleString()}</Row>}
          <Row icon={Building2} label="Vendor" testid="renewal-detail-vendor">
            {hasVendor ? (
              <div className="space-y-1">
                <div className="font-semibold">{v.company || i.vendor_name}</div>
                {v.contact_name && <div className="flex items-center gap-1.5 text-ink/80"><User className="h-3.5 w-3.5 text-ink/40" /> {v.contact_name}</div>}
                {v.phone && <a href={`tel:${v.phone}`} className="flex items-center gap-1.5 text-ink/80 hover:text-ink"><Phone className="h-3.5 w-3.5 text-ink/40" /> {v.phone}</a>}
                {v.email && <a href={`mailto:${v.email}`} className="flex items-center gap-1.5 text-ink/80 hover:text-ink break-all"><Mail className="h-3.5 w-3.5 text-ink/40" /> {v.email}</a>}
                {v.account_number && <div className="flex items-center gap-1.5 text-ink/80"><Hash className="h-3.5 w-3.5 text-ink/40" /> {v.account_number}</div>}
              </div>
            ) : <span className="text-ink/50">No vendor on file{readOnly ? "" : " — add one under Edit"}.</span>}
          </Row>
          <Row icon={StickyNote} label="Notes" testid="renewal-detail-notes">
            {i.notes ? <div className="whitespace-pre-wrap text-ink/80">{i.notes}</div> : <span className="text-ink/50">—</span>}
          </Row>
          <Row icon={BellRing} label="Reminders" testid="renewal-detail-reminders">
            <div className="space-y-1.5">
              <div className="text-ink/80">{nextReminderText(i)}</div>
              <div className="flex flex-wrap items-center gap-1.5">
                {leads.map((d) => (
                  <span key={d} data-testid={`renewal-detail-rung-${d}`} title={spent.has(d) ? "Already sent this cycle" : "Still to come"}
                    className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${spent.has(d) ? "border-sand-200 bg-sand-100 text-ink/40 line-through" : "border-ink/20 bg-white text-ink"}`}>{d}d</span>
                ))}
                <span className="text-[11px] text-ink/60">· once overdue, {overdueLabel(i.effective_overdue_every_days ?? 7)}{i.uses_default_reminders ? ` · ${V.lc("org")} default` : " · custom for this one"}</span>
              </div>
              {i.last_nudge_at && <div className="text-xs text-ink/50">Last reminder {fmtStamp(i.last_nudge_at)}</div>}
              {i.overdue_alerted_at && <div className="text-xs text-ink/50">Last overdue alert {fmtStamp(i.overdue_alerted_at)}{i.overdue_alert_count > 1 ? ` (${i.overdue_alert_count} so far)` : ""}</div>}
            </div>
          </Row>
          {/* The card only had room for "Notifies Jane + 2 more". Here there's space to name
              everyone and say which channel each person actually gets, so nobody has to guess
              whether a reminder really lands. */}
          <Row icon={Users} label="Notifies" testid="renewal-detail-notifies">
            {!i.notify ? <span className="text-ink/50">—</span> : (
              <div className="space-y-1.5">
                <div className="text-ink/80">{notifyScopeText(i.notify)}</div>
                {(i.notify.recipients || []).length > 0 && (
                  <div className="space-y-1">
                    {i.notify.recipients.map((r) => (
                      <div key={r.user_id} data-testid={`renewal-detail-recipient-${r.user_id}`} className="flex flex-wrap items-baseline gap-x-2 text-xs">
                        <span className="font-semibold text-ink">{r.name}</span>
                        {r.role && <span className="text-ink/50">{V.role(r.role)}</span>}
                        <span className="text-ink/60">· {chanText(r)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {(i.notify.externals || []).length > 0 && (
                  <div className="space-y-1">
                    {i.notify.externals.map((e) => (
                      <div key={e.email} data-testid={`renewal-detail-external-${e.email}`} className="flex flex-wrap items-baseline gap-x-2 text-xs">
                        <span className="font-semibold text-ink break-all">{e.email}</span>
                        <span className="text-ink/50">Outside address</span>
                        <span className="text-ink/60">· email</span>
                      </div>
                    ))}
                  </div>
                )}
                {!i.notify.count && (
                  <div className="text-xs text-ink/50">Reminders are switched off for everyone in scope — check Settings → Notifications.</div>
                )}
              </div>
            )}
          </Row>
          <Row icon={FileCheck2} label="History" testid="renewal-detail-history">
            {(i.history || []).length === 0 ? <span className="text-ink/50">No cycles filed yet.</span> : (
              <div className="space-y-1.5">
                {[...(i.history || [])].reverse().map((h, ri) => {
                  const idx = (i.history || []).length - 1 - ri;
                  return (
                    <div key={idx} className="text-xs text-ink/80 flex items-center gap-2 flex-wrap">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                      Due {h.was_due_on} · filed {h.completed_on} by {h.completed_by}{h.note ? ` — ${h.note}` : ""}
                      {h.proof?.filename && (
                        <a href={`${API}/tenants/${tenantId}/renewals/${i.id}/proof/${idx}`} target="_blank" rel="noreferrer"
                          className="font-bold text-redline-600 hover:text-redline-700 inline-flex items-center gap-1"><Paperclip className="h-3 w-3" /> {h.proof.filename}</a>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Row>
          <div className="text-[11px] text-ink/40 pt-3" data-testid="renewal-detail-meta">
            Added {fmtStamp(i.created_at)}{i.created_by ? ` by ${i.created_by}` : ""}{i.source === "starter" ? " · from Commonly tracked" : ""}{i.updated_at ? ` · updated ${fmtStamp(i.updated_at)}` : ""}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
